import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const BranchContext = createContext(null);

export function BranchProvider({ children }) {
    const [branches, setBranches] = useState([]);
    const [hasBranches, setHasBranches] = useState(false);
    const [selectedBranchId, setSelectedBranchId] = useState(() => {
        return localStorage.getItem("trulyn_branch") || null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/branches");
                const data = res.data || [];
                setBranches(data);
                setHasBranches(data.length > 0);
                // Clear stale branch selection when client has no branches
                // or when saved branch no longer exists in the list
                if (data.length === 0 && selectedBranchId) {
                    setSelectedBranchId(null);
                    localStorage.removeItem("trulyn_branch");
                } else if (data.length > 0 && selectedBranchId) {
                    if (!data.find(b => b.id === selectedBranchId)) {
                        setSelectedBranchId(null);
                        localStorage.removeItem("trulyn_branch");
                    }
                }
            } catch {
                setBranches([]);
                setHasBranches(false);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const selectBranch = useCallback((branchId) => {
        setSelectedBranchId(branchId);
        if (branchId) {
            localStorage.setItem("trulyn_branch", branchId);
        } else {
            localStorage.removeItem("trulyn_branch");
        }
    }, []);

    // Helper: returns query params object for API calls (memoized to avoid re-renders)
    const branchParam = useMemo(
        () => (selectedBranchId ? { branch_id: selectedBranchId } : {}),
        [selectedBranchId]
    );

    const selectedBranch = branches.find(b => b.id === selectedBranchId) || null;

    return (
        <BranchContext.Provider value={{
            branches,
            hasBranches,
            selectedBranchId,
            selectedBranch,
            selectBranch,
            branchParam,
            loading,
        }}>
            {children}
        </BranchContext.Provider>
    );
}

export function useBranch() {
    const ctx = useContext(BranchContext);
    if (!ctx) throw new Error("useBranch must be used within BranchProvider");
    return ctx;
}
